import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction, useState, useEffect } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components/general/Table"
import { Role, Search, UserType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import { Localstorage, formatPhoneNumber } from "@/lib/services"
import { validators } from "@/lib/validators"
import { useMutation } from "react-query"
import { api } from "@/lib/api"
import { LocalStorage } from "@/lib/types/LocalStorage"
import { Toggle } from "@/components/general/Toggle"
import Swal from "sweetalert2"
import { Dropdown, SearchUser } from "@/components/general/Dropdown"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { users: UserType[] }
  handleForm: (form: UserType) => void
  handleFormUpdate: (form: UserType) => void
  handleFormDelete: (form: string) => void | Promise<unknown>
  header: {
    name: string
    width: string
  }[]
  role: Role
  salesRepId: string
  slug: string
  title: string
  setTitle: (title: string) => void
}

export const Users: FC<Props> = (props) => {
  const [salesRep, setSalesRep] = useState<SearchUser>()
  const [salesRepName, setSalesRepName] = useState("")
  const [approved, setApproved] = useState(false)
  const [crmCheck, setCrmCheck] = useState(false)
  const user = Localstorage.get<LocalStorage>("next.user")

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UserType>()

  const [search, setSearch] = useState({
    input: "",
    userRole: props.role,
    salesRepID: props.salesRepId,
  })

  useEffect(() => {
    setSearch((prevStats) => ({
      ...prevStats,
      salesRepID: props.salesRepId,
    }))
  }, [props.salesRepId])

  const mutation = useMutation((payload: Search) => {
    return api.AdminApi.Search(payload)
  })

  useEffect(() => {
    mutation.mutate({
      input: "",
      userRole: "SALESREP",
      salesRepID: "",
    })
  }, [])

  const onSubmit = (data: UserType) => {
    if (data.userID != undefined) {
      data.approved = approved
      data.crmCheck = crmCheck
      data.salesRepId = salesRep?.id as string
      props.handleFormUpdate(data)
    } else {
      data.approved = approved
      data.crmCheck = crmCheck
      data.salesRepId = salesRep?.id as string
      props.handleForm(data)
    }
  }

  const replaceNullWithDefault = (input: any, defaultValue: string) => {
    return input !== null ? input : defaultValue
  }

  const populateData = (e: UserType) => {
    props.setTitle('Update User')
    if (e.salesRepId == null) {
      const notAssigned = replaceNullWithDefault(e.salesRepId, "-")
      setSalesRepName(notAssigned)
      setValue("salesRepId", notAssigned)
    } else {
      setValue("salesRepId", e.salesRepId)
      setSalesRep({
        id: e.salesRepId,
        firstName: e.salesRep.firstName,
        lastName: e.salesRep.lastName,
        email: e.email as string,
      })
      setSalesRepName(e.salesRep.firstName + " " + e.salesRep.lastName)
    }
    setValue("userID", e.id)
    setValue("firstName", e.firstName)
    setValue("lastName", e.lastName)
    setValue("email", e.email)
    setValue("phone", e.phone)
    setValue("companyName", e.companyName)
    setValue("role", e.role)
    setApproved(e.approved)
    setCrmCheck(e.crmCheck)
    setValue("markUp", e.markUp)
    setValue("operationEmail", e.operationEmail == "" ? "-" : e.operationEmail)
    setValue("crmEmail", e.crmEmail == "" ? "-" : e.crmEmail)
    setValue("crmCheck", e.crmCheck)
    props.setPopup(true)
  }


  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      color: "#ffffff",
      background: "#333333",
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (!result.isConfirmed) return

      try {
        // wait for the request itself: a toast fired before this resolves
        // claims success even when the server rejected the delete
        await props.handleFormDelete(id)
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Could not delete user",
          text: error instanceof Error ? error.message : "Please try again.",
          toast: true,
          position: "top",
          timer: 4000,
          iconColor: "#a33333",
          showCancelButton: false,
          showConfirmButton: false,
        })
        return
      }

      Swal.fire({
        icon: "success",
        title: "Record Deleted",
        // background: "#333333",
        toast: true,
        position: "top",
        timer: 2000,
        iconColor: "#CEA16B",
        showCancelButton: false,
        showConfirmButton: false,
        customClass: {
          popup: "custom-success-popup",
        }
      })
    })
  }

  useResetOnClose(props.popup, () => {
    reset({
      id: undefined,
      userID: undefined,
      role: undefined,
      salesRepId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      companyName: "",
      approved: false,
      markUp: 0,
      crmCheck: false,
      operationEmail: "",
      crmEmail: "",
    })
    setSalesRep(undefined)
    setSalesRepName("")
    setApproved(false)
    setCrmCheck(false)
    setSearch({ input: "", userRole: props.role, salesRepID: props.salesRepId })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="medium"
        title={props.title}
      >
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">First Name</label>
              <input
                className={
                  (errors.firstName ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="text"
                {...register("firstName", validators.firstName)}
              />
              {errors.firstName && (
                <div className="text-xs text-red-700 dark:text-red-400">
                  {errors.firstName?.message}
                </div>
              )}
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Last Name</label>
              <input
                className={
                  (errors.lastName ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="text"
                {...register("lastName", validators.lastName)}
              />
              {errors.lastName && (
                <div className="text-xs text-red-700 dark:text-red-400">
                  {errors.lastName?.message}
                </div>
              )}
            </fieldset>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Email</label>
              <input
                className={
                  (errors.email ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="text"
                {...register("email", validators.email)}
              />
              {errors.email && (
                <div className="text-xs text-red-700 dark:text-red-400">
                  {errors.email?.message}
                </div>
              )}
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Phone</label>
              <input
                className={
                  (errors.phone ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="text"
                {...register("phone", validators.phone)}
              />
              {errors.phone && (
                <div className="text-xs text-red-700 dark:text-red-400">
                  {errors.phone?.message}
                </div>
              )}
            </fieldset>
          </div>
          <div className="grid grid-cols-2 gap-2 ">
            <div className="flex flex-col justify-evenly pl-1">
              <label className="text-xs dark:text-primary-8 text-light-1">Authorized</label>
              <Toggle
                toggled={approved}
                onClick={(state: boolean) => setApproved(state)}
              />
            </div>

            <div className="flex flex-col justify-evenly pl-1">
              <label className="text-xs dark:text-primary-8 text-light-1">Crm Check</label>
              <Toggle
                toggled={crmCheck}
                onClick={(state: boolean) => setCrmCheck(state)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <fieldset className="col-span-1">
              <label className="text-xs dark:text-primary-8 text-light-1">Company</label>
              <input
                className={
                  (errors.companyName ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="text"
                {...register("companyName", validators.company)}
              />
              {errors.companyName && (
                <div className="text-xs text-red-700 dark:text-red-400">
                  {errors.companyName?.message}
                </div>
              )}
            </fieldset>
            <fieldset className="col-span-1">
              <label className="text-xs dark:text-primary-8 text-light-1">Mark Up</label>
              <input
                className={
                  (errors.markUp ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500"
                }
                type="number"
                {...register("markUp", { required: true })}
              />
              {errors.markUp && (
                <div className="text-xs text-red-700 dark:text-red-400">
                  {errors.markUp?.message}
                </div>
              )}
            </fieldset>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-1">
              <label className="text-xs dark:text-primary-8 text-light-1">Role</label>
              <select
                className={
                  (errors.role ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                {...register("role", { required: true })}
                defaultValue=""
                disabled={user?.role == "SALESREP"}
              >
                <option className="dark:bg-primary-1" value="" disabled>
                  Select Role
                </option>
                <option className="dark:bg-primary-1" value="ADMIN">
                  Admin
                </option>
                <option className="dark:bg-primary-1" value="CUSTOMER">
                  Customer
                </option>
                <option className="dark:bg-primary-1" value="SALESREP">
                  Salesrep
                </option>
              </select>
            </div>
            {errors.role && (
              <div className="text-xs text-red-700 dark:text-red-400">
                {errors.role?.message}
              </div>
            )}
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Assign Sales Rep</label>
              <Dropdown
                isCheck={true}
                isSalesRep={true}
                search={search}
                handleSearch={(input) => mutation.mutate(input)}
                listingOfUsers={
                  mutation.data == undefined
                    ? null
                    : mutation.data.topMatchingUsers
                }
                selectedUser={salesRep}
                setSelectedUser={setSalesRep}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">
                Assigned Sales Rep
              </label>
              <input
                className=" w-full dark:border-secondary-2 text-sm p-2 dark:bg-transparent outline-none dark:text-white bg-white border-2 rounded dark:placeholder:text-zinc-500 placeholder:text-light-2"
                value={salesRepName}
                readOnly
                type="text"
                disabled
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Operations Email</label>
              <input
                className={
                  (errors.operationEmail
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent outline-none bg-white dark:text-white border-2 rounded placeholder:text-zinc-500"
                }
                type="text"
                {...register("operationEmail")}
              />
              {errors.operationEmail && (
                <div className="text-xs text-red-700 dark:text-red-400">
                  {errors.operationEmail?.message}
                </div>
              )}
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Crm Email</label>
              <input
                className={
                  (errors.crmEmail ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent outline-none bg-white dark:text-white border-2 rounded placeholder:text-zinc-500"
                }
                type="text"
                {...register("crmEmail")}
              />
               {errors.crmEmail && (
                <div className="text-xs text-red-700 dark:text-red-400">
                  {errors.crmEmail?.message}
                </div>
              )}
            </fieldset>
          </div>
          <button
            onClick={handleSubmit(onSubmit)}
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full dark:text-secondary-1 text-primary-1"
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.users.map((e, i) => (
            <tr key={i} className="text-left">
              <td
                className={`whitespace-nowrap p-4 w-40 ${e.approved === false ? "text-red-600" : ""
                  }`}
              >
                {e.firstName} {e.lastName}
              </td>

              <td className={"whitespace-nowrap p-4 w-80"}>{e.email}</td>
              <td className={"whitespace-nowrap p-4 w-40"}>
                {formatPhoneNumber(e.phone)}
              </td>
              <td className={"whitespace-nowrap p-4 w-60"}>{e.companyName}</td>
              <td className={"whitespace-nowrap p-4 w-32"}>{e.role}</td>
              <td className={"whitespace-nowrap text-right p-4 w-24"}>
                <p>{e.markUp}</p>
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  {/* the DELETE /user/:id route is admin-only */}
                  {user?.role === "ADMIN" && (
                    <TrashIcon
                      className="w-4 h-4 text-accent cursor-pointer"
                      onClick={() => onDelete(e.id)}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
